import { useMemo, useState } from 'react';

//* HOCs
import { withLanguageContext, withUIContext } from 'context';

//* Components
import Icon from 'components/common/Icon';
import CustomLink from 'components/common/CustomLink';
import ButtonLoading from 'components/common/ButtonLoading';
import LottieAnimation from 'components/common/LottieAnimation';

//* Style
import ButtonStyle from './style';

//* Lottie file
import arrow from 'lottieFiles/arrow.json';

const Button = ({
	text,
	type,
	color,
	background,
	borderColor,
	hoverColor,
	hoverBorderColor,
	hoverBackground,
	icon,
	className,
	onClick,
	children,
	url,
	customLinkProps,
	translate,
	winWidth,
	screenSizes,
	target,
	disabled,
	isBtnLoad = false,
}) => {
	//! States
	const [arrowLottie, setArrowLottie] = useState();

	const Component = useMemo(() => (url ? CustomLink : 'button'), [url]);
	const customProps = useMemo(() => (url ? { ...customLinkProps, url, target } : {}), []);

	let btn;

	switch (type) {
		case 'oval':
			btn = (
				<ButtonStyle
					color={color}
					borderColor={color}
					background={background}
					hoverColor={hoverColor}
					hoverBackground={background}
					hoverBorderColor={hoverBorderColor}
					className={`oval font-ping-medium p pS ${type} ${disabled ? 'disabled' : ''} ${className || ''}`}
					onClick={onClick}>
					<Component {...customProps}>
						<span className='btn-content'>{children || translate(text)}</span>
					</Component>
				</ButtonStyle>
			);
			break;
		case 'back':
			btn = (
				<ButtonStyle
					color={color}
					onClick={onClick}
					hoverColor={color}
					className={`back font-helvetica p ${type} ${disabled ? 'disabled' : ''} ${className || ''}`}>
					<Component {...customProps}>
						<Icon name='arrow' />

						{children || translate(text)}
					</Component>
				</ButtonStyle>
			);
			break;
		case 'lottie':
			btn = (
				<ButtonStyle
					color={color}
					onClick={onClick}
					hoverColor={color}
					background={background}
					hoverBackground={background}
					onMouseEnter={() => setArrowLottie(true)}
					onMouseLeave={() => setArrowLottie(false)}
					className={`lottie font-ping-bolder p ${type} ${disabled ? 'disabled' : ''} ${className || ''}`}>
					<Component {...customProps}>
						<span className='btn-content'>{children || translate(text)}</span>

						{winWidth < screenSizes.desktopSizeXS ? (
							<LottieAnimation
								animData={arrow}
								autoplay={false}
								isPlay={false}
								loop={false}
							/>
						) : (
							<LottieAnimation
								animData={arrow}
								isPlay={arrowLottie}
								isReverse={!arrowLottie}
								autoplay={false}
								loop={false}
							/>
						)}
					</Component>
				</ButtonStyle>
			);
			break;
		case 'icon-aside':
			btn = (
				<ButtonStyle
					color={color}
					onClick={onClick}
					background={background}
					hoverColor={hoverColor}
					hoverBackground={hoverBackground}
					className={`icon-aside p pS ${type} ${disabled ? 'disabled' : ''} ${className || ''}`}>
					<Component {...customProps}>
						<span className='font-ping-bolder btn-content'>
							<span className='button-text'>{children || translate(text)}</span>
						</span>

						<span className='btn-content btn-icon'>
							<Icon
								name={'arrow'}
								className={`p pS ${isBtnLoad ? 'hide' : ''}`}
							/>

							{isBtnLoad ? <ButtonLoading /> : null}
						</span>
					</Component>
				</ButtonStyle>
			);
			break;
		default:
			btn = (
				<ButtonStyle
					color={color}
					onClick={onClick}
					background={background}
					hoverColor={hoverColor}
					borderColor={borderColor}
					hoverBackground={hoverBackground}
					hoverBorderColor={hoverBorderColor}
					className={`default for-footer font-ping-bold p ${icon ? 'pS' : 'pXS'} ${type || ''} ${disabled ? 'disabled' : ''} ${className || ''}`}>
					<Component {...customProps}>
						<span className='btn-content'>
							{children || <span className={`footer-text ${isBtnLoad ? 'hide' : ''}`}>{translate(text)}</span>}

							{icon && (
								<Icon
									className='p'
									name={icon}
								/>
							)}

							{isBtnLoad ? <ButtonLoading /> : null}
						</span>
					</Component>
				</ButtonStyle>
			);
	}

	return btn;
};

export default withUIContext(withLanguageContext(Button, ['translate', 'selectedLang']), ['winWidth', 'screenSizes']);
